import { Stack, Typography } from '@mui/material'
import {
  activationShareFormula,
  predictedAnnualValueFormula,
  predictedArrFormula,
  tagMarginFormula,
  percentLabel,
} from '@repo/types'

import { InfoTooltip } from 'components/feedback/InfoTooltip'
import type { SalesDealCard } from 'services'

import { money, moneyShort } from './dealsModel'

/**
 * VRG-736 — a deal's three value figures (Spencer, 2026-10-22), each with a tooltip showing its
 * formula WITH the deal's own numbers and rates and where each rate comes from:
 *
 *   predicted ARR          = plan × 12 + activations on (production × activation share)
 *   tag margin / yr        = expected activations × tag unit cost × markup   (never in ARR)
 *   predicted annual value = predicted ARR + tag margin
 *
 * The words are `@repo/types` `deal-value-formula.ts` (shared with the API guard), the definition
 * footer is the glossary term (§44): the tooltip body is the local formula, the footer the term.
 * One component for the drawer, the card and the Deals table so the three never disagree.
 */
type Size = 'row' | 'compact'

const Figure = ({ text, term, formula, size, testId, muted }: { text: string; term: string; formula: string; size: Size; testId: string; muted?: boolean }) => (
  <Stack direction="row" spacing={0.5} alignItems="center" sx={{ minWidth: 0 }}>
    <Typography
      variant="body3"
      noWrap={size === 'compact'}
      sx={{ fontWeight: size === 'row' && !muted ? 600 : 400 }}
      color={muted ? 'text.secondary' : 'text.primary'}
      data-testid={testId}
    >
      {text}
    </Typography>
    <InfoTooltip term={term} text={formula} />
  </Stack>
)

const usdText = (n: number | null | undefined, size: Size) => (n == null ? '—' : size === 'compact' ? moneyShort(n) : `${money(n)} / yr`)

export const PredictedArrFigure = ({ deal, size = 'row' }: { deal: SalesDealCard; size?: Size }) => (
  <Figure
    term="deal-anticipated-value"
    testId={`deal-arr-${deal.id}`}
    size={size}
    muted={deal.value.amount === null}
    text={deal.value.amount === null ? '—' : size === 'compact' ? moneyShort(deal.value.amount, deal.value.currency) : `${money(deal.value.amount, deal.value.currency)} / yr`}
    formula={deal.arr_breakdown ? predictedArrFormula(deal.arr_breakdown) : (deal.value.detail ?? 'No calculator, form or manual value yet.')}
  />
)

export const TagMarginFigure = ({ deal, size = 'row' }: { deal: SalesDealCard; size?: Size }) => (
  <Figure
    term="tag-margin"
    testId={`deal-tags-${deal.id}`}
    size={size}
    muted={deal.tag_margin == null}
    text={usdText(deal.tag_margin, size)}
    formula={tagMarginFormula(deal.potential?.tag_margin ?? null)}
  />
)

export const AnnualValueFigure = ({ deal, size = 'row' }: { deal: SalesDealCard; size?: Size }) => (
  <Figure
    term="predicted-annual-value"
    testId={`deal-annual-${deal.id}`}
    size={size}
    muted={deal.predicted_annual_value == null}
    text={usdText(deal.predicted_annual_value, size)}
    formula={predictedAnnualValueFormula(deal.value, deal.potential?.tag_margin ?? null, deal.predicted_annual_value ?? null)}
  />
)

export const ActivationShareFigure = ({ deal }: { deal: SalesDealCard }) => {
  const p = deal.potential
  if (!p) return null
  return (
    <Figure
      term="activation-share"
      testId={`deal-share-${deal.id}`}
      size="row"
      text={`${percentLabel(p.activation_share.share)}${p.expected_activations != null ? ` · ${Math.round(p.expected_activations).toLocaleString('en-US')} activations / yr` : ''}`}
      formula={activationShareFormula(p.inputs.units, p.activation_share, p.expected_activations)}
    />
  )
}
